import React, { ReactNode, useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { ring } from "ldrs";
import { ProfileContext } from "./ProfileContext";
import { validateUserDomain, getDomainValidationMessage } from "./domainConfig";
ring.register();

interface LogoutHeaderButtonProps {
  className?: string;
}

interface AuthContentProps {
  children: ReactNode;
}

interface AuthWrapperProps {
  children: ReactNode;
}

const centerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100vw",
  height: "100vh",
};

const buttonStyle: React.CSSProperties = {
  padding: "20px 40px",
  fontSize: "24px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  color: "red",
  textAlign: "center",
  marginTop: "50px",
};

export const LogoutHeaderButton: React.FC<LogoutHeaderButtonProps> = ({
  className,
}) => {
  const { instance } = useMsal();

  const handleLogout = () => {
    // Clear local storage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("tutorialShown");
    
    // Logout with proper redirect
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    }).catch((e) => console.error(e));
  };

  return (
    <a onClick={handleLogout} className={className} style={{ cursor: "pointer" }}>
      Log out
    </a>
  );
};

const AuthContent: React.FC<AuthContentProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<any>(null);
  const [isDomainAllowed, setIsDomainAllowed] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["api://32191e63-e253-48de-9ea1-a5337e236fe6/.default"],
          account: accounts[0],
        });

        const accessToken = response.accessToken;

        // Update Dev API Endpoint
        const endpoint =
          "https://dev-api.myenterprise.ai/daisy/general/v1/users/entra/profile";

        const result = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        // Validate user domain before setting profile data
        console.log('Validating domain for email:', result.data?.email_address);
        if (result.data?.email_address && !validateUserDomain(result.data.email_address)) {
          console.log('Domain validation failed for:', result.data.email_address);
          setError(getDomainValidationMessage(result.data.email_address));
          // Clear any existing profile data
          setProfileData(null);
          setIsDomainAllowed(false);
          // Logout the unauthorized user
          setTimeout(() => {
            instance.logoutRedirect({
              postLogoutRedirectUri: window.location.origin
            });
          }, 3000); // Give user 3 seconds to read the message
          return;
        }
        
        console.log('Domain validation passed for:', result.data?.email_address);

        // User is authorized
        setProfileData(result.data);
        setIsDomainAllowed(true);
        localStorage.setItem("accessToken", accessToken);
        console.log("Profile Data:", result.data);
      } catch (err: any) {
        console.error("Error acquiring token or fetching data:", err);
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !profileData && !error) {
      fetchProfileData();
    }
  }, [isAuthenticated, instance, accounts, profileData, error]);

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div style={centerStyle}>
        <l-ring
          size="80"
          stroke="5"
          bg-opacity="0"
          speed="2"
          color="black"
        ></l-ring>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...centerStyle,
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          color: '#dc3545',
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '15px'
        }}>
          Access Restricted
        </div>
        <div style={{
          color: '#6c757d',
          fontSize: '16px',
          marginBottom: '20px',
          maxWidth: '500px',
          lineHeight: '1.5'
        }}>
          {error}
        </div>
        <div style={{
          color: '#6c757d',
          fontSize: '14px'
        }}>
          You will be redirected to the login page in a few seconds...
        </div>
      </div>
    );
  }

  // Only render children if user is authenticated AND domain is allowed
  if (!isDomainAllowed) {
    return (
      <div style={{
        ...centerStyle,
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          color: '#6c757d',
          fontSize: '16px'
        }}>
          Verifying access...
        </div>
      </div>
    );
  }

  return <ProfileContext.Provider value={{ 
    profileData, 
    setProfileData, 
    isDomainAllowed, 
    userDomain: profileData?.email_address ? profileData.email_address.split('@')[1] : null 
  }}>
  {children}
  </ProfileContext.Provider>;
};

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  return <AuthContent>{children}</AuthContent>;
};

export default AuthWrapper;
